import React from "react";
import { useOfflineCacheStatus } from "../state/OfflineCacheStatusContext";

const formatProgressLabel = (completed: number, total: number, currentKey: string | null): string => {
  if (total <= 0) return "Caching offline resources…";
  const inFlight = Boolean(currentKey);
  const effective = inFlight ? Math.min(completed + 1, total) : Math.min(completed, total);
  const prefix = inFlight ? "Caching" : "Cached";
  return `${prefix} ${effective} of ${total} offline resources…`;
};

export const OfflineCacheProgressOverlay: React.FC = () => {
  const { state, total, completed, currentKey, error } = useOfflineCacheStatus();

  if (state === "prefetching") {
    const effective = currentKey ? Math.min(completed + 1, total) : Math.min(completed, total);
    const progressPercent = total > 0 ? Math.min(100, Math.round((effective / total) * 100)) : 0;
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
          <div className="flex items-center gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center">
              <span className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900">Preparing offline mode</h3>
              <p className="text-sm text-slate-600">{formatProgressLabel(completed, total, currentKey)}</p>
              {currentKey && (
                <p className="mt-1 truncate text-xs font-mono uppercase tracking-wide text-slate-400">
                  {currentKey}
                </p>
              )}
            </div>
          </div>
          {total > 0 && (
            <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-indigo-500 transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (state === "success") {
    return (
      <div className="fixed bottom-4 right-4 z-[9999] rounded-xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-lg">
        Offline resources cached. You can work safely without a connection.
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="fixed bottom-4 right-4 z-[9999] rounded-xl bg-rose-500 px-4 py-3 text-sm font-semibold text-white shadow-lg">
        {error ?? "Offline caching failed. Stay online and try again, or continue working — new records will queue for sync."}
      </div>
    );
  }

  return null;
};
